import { useEffect, useMemo, useRef, useState } from 'preact/hooks'
import useDetectBreakpoint from 'hooks/useDetectBreakpoint'
import { AnimatePresence, motion } from 'motion/react'
import clsx from 'clsx'
import throttle from 'lodash.throttle'
import { v4 as uuidv4 } from 'uuid'
import ProductCarousel from 'snippets/product/product-carousel/product-carousel'
import CarouselSwiper from 'modules/carousel-swiper/carousel-swiper'

const selectors = {
  HIDDEN_CLASS: 'hidden',
  THROTTLE_TIME: 100
}

function CarouselTabs({
  tabs,
  products,
  productConfig,
  selectedTab,
  hasUnderBorder,
  setSelectedTab,
  refProductTab,
  classCarouselWrapper,
  classCarouselTabWrapper = 'pt-5 md:pt-7',
  options = null,
  isCustomProductCard = false,
  isHeadingNative = true,
  classCarouselHeader = 'relative w-full'
}) {
  const [contentHeight, setContentHeight] = useState(0)
  const refProductContent = useRef(null)
  const [carouselHeading, setCarouselHeading] = useState(null)
  const swiperSelector = `js-carousel-${uuidv4()}`
  const swiperSelectorHeading = `js-carousel-tabs-${uuidv4()}`
  const { isDesktop } = useDetectBreakpoint()
  const visibleTabs = useMemo(() => tabs.filter((item) => productConfig[item]?.length > 0), [tabs, productConfig])
  const isLastTab = useMemo(() => tabs.indexOf(selectedTab) === tabs.length - 1, [tabs, selectedTab])

  const optionsProductsCarousel = options || {
    slidesPerView: 2,
    spaceBetween: 1,
    observeSlideChildren: true,
    breakpoints: {
      768: {
        slidesPerView: 4
      }
    }
  }

  const optionsHeadingCarousel = {
    slidesPerView: 'auto',
    spaceBetween: 12
  }

  const handleTabClick = (item, index) => {
    setSelectedTab(item)
    carouselHeading?.slideTo(index)
  }

  const renderHeadingNative = () => (
      <ul className="container relative mb-4 flex items-center gap-3 md:mb-6">
        {visibleTabs.map(
          (item) =>
            <motion.li key={item} className="relative">
              <button
                type="button"
                onClick={() => setSelectedTab(item)}
                className={clsx(
                  'relative cursor-pointer text-xs font-extrabold uppercase focus:outline-none hover:text-neutral-900 transition-colors duration-300 md:text-sm',
                  item === selectedTab ? 'text-neutral-900' : 'text-neutral-500'
                )}
              >
                {item}
                {item === selectedTab && hasUnderBorder ? (
                  <motion.div
                    layoutId="underline"
                    className="absolute bottom-0 left-0 right-0 h-0.5 bg-neutral-900"
                  />
                ) : null}
              </button>
            </motion.li>
        )}
      </ul>
    )

  const renderHeadingCarousel = () => (
    <div className={classCarouselHeader}>
      <CarouselSwiper
        options={optionsHeadingCarousel}
        scrollbar={false}
        selector={swiperSelectorHeading}
        className="mb-4 md:mb-6"
        setCarouselInstance={setCarouselHeading}
      >
        {visibleTabs.map((item, index) => (
          <swiper-slide
            key={item}
            className="w-fit first:ml-5 lg:first:ml-10 lg:last:mr-10"
          >
            <button
              type="button"
              onClick={() => handleTabClick(item, index)}
              className={clsx(
                'text-sm font-extrabold uppercase',
                selectedTab === item ? 'border-b-2 border-neutral-900' : ''
              )}
            >
              {item}
            </button>
          </swiper-slide>
        ))}
      </CarouselSwiper>
      <div className={clsx('absolute right-0 top-5 z-2 h-6 w-[154px] flex items-center pointer-events-none md:top-7 md:w-[174px]', isLastTab && 'hidden')}>
        <div className="h-full w-12 bg-slide-gradient" />
        <div className="h-full flex-1 bg-white" />
      </div>
    </div>
  )

  useEffect(() => {
    if (!refProductContent.current || contentHeight !== 0) return
    setContentHeight(refProductContent.current?.scrollHeight || 0)

    const throttledUpdate = throttle(() => {
      refProductContent.current.style.minHeight = ''
      setContentHeight(refProductContent.current?.scrollHeight || 0)
    }, selectors.THROTTLE_TIME)

    window.addEventListener('resize', throttledUpdate)

    // eslint-disable-next-line consistent-return
    return () => window.removeEventListener('resize', throttledUpdate)
  }, [products])

  return (
    <div className={classCarouselTabWrapper} ref={refProductTab}>
      {isHeadingNative ? renderHeadingNative() : renderHeadingCarousel()}
      <div className="relative z-3" ref={refProductContent} style={{ minHeight: contentHeight }}>
        <AnimatePresence mode="wait">
          <motion.div
            key={selectedTab}
            initial={{ opacity: 0, y: 1 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -1 }}
            transition={{ duration: 0.4 }}
          >
            {products.length > 0 && (
              <ProductCarousel
                key={selectedTab}
                classWrapper={classCarouselWrapper}
                selector={swiperSelector}
                products={products}
                options={optionsProductsCarousel}
                navigation
                className="relative"
                classItemSlide="h-auto"
                textButton='Add'
                isDesktop={isDesktop}
                isCustomProductCard={isCustomProductCard}
              />
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  )
}

export default CarouselTabs
